import { useShowDialog } from '@experiences/util';
import {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSWRConfig } from 'swr';

import { notificationType } from '../../../../common/constants/Constant';
import { useUiSnackBar } from '../../../../common/hooks/useUiSnackBar';
import { generateMLServiceKey } from '../../../../services/licensing/LicenseService';
import { generateConsumablesFetchKey } from '../../subcomponents/LicensingDataFetchKeyGenerators';

// Confirm → regenerate an ML service API key → refresh the consumables SWR cache (org or tenant scoped).
export const useRegenerateMlKey = (tenantId?: string) => {
    const { formatMessage: translate } = useIntl();
    const { createNotification } = useUiSnackBar();
    const createDialog = useShowDialog();
    const { mutate } = useSWRConfig();
    const [ loading, setLoading ] = useState(false);
    const consumablesFetchKey = useMemo(() => generateConsumablesFetchKey(tenantId), [ tenantId ]);

    const regenerate = useCallback(async (keyType?: string) => {
        if (!keyType) {
            return;
        }
        const proceed = await createDialog({
            title: translate({ id: 'CLIENT_CONFIRM_GENERATION_REQUEST' }),
            body: translate({ id: 'CLIENT_CONFIRM_GENERATION_TEXT' }),
            icon: 'warning',
            showCancel: true,
            primaryButtonText: translate({ id: 'CLIENT_GENERATE' }),
        });
        if (!proceed) {
            return;
        }
        setLoading(true);
        try {
            await generateMLServiceKey(keyType);
            await mutate((key: unknown) => {
                if (key === consumablesFetchKey) {
                    return true;
                }
                if (typeof key !== 'object' || key == null) {
                    return false;
                }
                const swrKey = key as { url?: string; tenantGlobalId?: string };
                return swrKey.url === consumablesFetchKey && (tenantId == null || swrKey.tenantGlobalId === tenantId);
            });
        } catch {
            createNotification(translate({ id: 'CLIENT_UNKNOWN_ERROR' }), notificationType.ERROR);
        } finally {
            setLoading(false);
        }
    }, [ consumablesFetchKey, createDialog, createNotification, mutate, tenantId, translate ]);

    return {
        loading,
        regenerate,
    };
};
